import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { GitCompare, TrendingUp, TrendingDown, Equal, AlertCircle, BarChart3, Search, Filter, X, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { CrossPatternComparisonService } from '@/services/CrossPatternComparisonService';
import { useIsMobile } from '@/hooks/use-mobile';

interface ComparisonSourceInfo {
  type: 'trading_file' | 'honest_simulation';
  id: string;
  name: string;
  analysis_mode?: string;
}

interface PatternData {
  pattern: string;
  frequency: number;
  success_rate: number;
  next_win_count: number;
  next_loss_count: number;
  total_sequences: number;
}

interface PatternComparisonResult {
  pattern: string;
  source1_data?: PatternData;
  source2_data?: PatternData;
  status: 'identical' | 'different_values' | 'only_in_source1' | 'only_in_source2';
  differences?: {
    frequency_diff?: number;
    success_rate_diff?: number;
    total_sequences_diff?: number;
  };
}

interface ComparisonSummary {
  total_patterns_source1: number;
  total_patterns_source2: number;
  identical_patterns: number;
  different_value_patterns: number;
  only_in_source1: number;
  only_in_source2: number;
  comparison_details: PatternComparisonResult[];
}

export const CrossPatternComparisonTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [tradingFiles, setTradingFiles] = useState<Array<{id: string, filename: string, analysis_modes: string[]}>>([]);
  const [honestSimulations, setHonestSimulations] = useState<Array<{id: string, name: string, filename: string}>>([]);
  const [source1, setSource1] = useState<ComparisonSourceInfo | null>(null);
  const [source2, setSource2] = useState<ComparisonSourceInfo | null>(null);
  const [selectedSource1Type, setSelectedSource1Type] = useState<'trading_file' | 'honest_simulation'>('trading_file');
  const [selectedSource2Type, setSelectedSource2Type] = useState<'trading_file' | 'honest_simulation'>('trading_file');
  const [selectedSource1Mode, setSelectedSource1Mode] = useState<string>('basic');
  const [selectedSource2Mode, setSelectedSource2Mode] = useState<string>('basic');
  const [comparisonResult, setComparisonResult] = useState<ComparisonSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [minSuccessRate, setMinSuccessRate] = useState<string>('');
  const [maxSuccessRate, setMaxSuccessRate] = useState<string>('');
  const [minFrequency, setMinFrequency] = useState<string>('');
  const [maxFrequency, setMaxFrequency] = useState<string>('');
  const [sortField, setSortField] = useState<string>('pattern');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Load available files and simulations
  useEffect(() => {
    loadAvailableData();
  }, []);

  const loadAvailableData = async () => {
    setIsLoadingData(true);
    try {
      const [filesResult, simulationsResult] = await Promise.all([
        CrossPatternComparisonService.getTradingFiles(),
        CrossPatternComparisonService.getHonestBacktestSimulations()
      ]);

      if (filesResult.success && filesResult.data) {
        setTradingFiles(filesResult.data);
      }

      if (simulationsResult.success && simulationsResult.data) {
        setHonestSimulations(simulationsResult.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load available files and simulations');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleComparePatterns = async () => {
    if (!source1 || !source2) {
      toast.error('Please select both sources for comparison');
      return;
    }

    if (source1.id === source2.id && source1.type === source2.type && source1.analysis_mode === source2.analysis_mode) {
      toast.error('Cannot compare the same source with itself');
      return;
    }

    setIsLoading(true);
    try {
      const result = await CrossPatternComparisonService.comparePatterns(source1, source2);
      
      if (result.success && result.data) {
        setComparisonResult(result.data);
        toast.success('Pattern comparison completed');
      } else {
        toast.error(result.error || 'Comparison failed');
      }
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error('Failed to compare patterns');
    } finally {
      setIsLoading(false);
    }
  };

  const renderSource1Selection = () => {
    const availableSources = selectedSource1Type === 'trading_file' ? tradingFiles : honestSimulations;
    const selectedFile = selectedSource1Type === 'trading_file' 
      ? tradingFiles.find(f => f.id === source1?.id)
      : null;

    return (
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium">Source 1 Type</label>
          <Select 
            value={selectedSource1Type} 
            onValueChange={(value: 'trading_file' | 'honest_simulation') => {
              setSelectedSource1Type(value);
              setSource1(null);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="trading_file">Trading Pattern File</SelectItem>
              <SelectItem value="honest_simulation">Honest Backtest Simulation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium">
            {selectedSource1Type === 'trading_file' ? 'Trading File' : 'Simulation'}
          </label>
          <Select 
            value={source1?.id || ''} 
            onValueChange={(value) => {
              const sourceData = availableSources.find(s => s.id === value);
              if (sourceData) {
                setSource1({
                  type: selectedSource1Type,
                  id: value,
                  name: selectedSource1Type === 'trading_file' 
                    ? (sourceData as any).filename 
                    : (sourceData as any).name,
                  analysis_mode: selectedSource1Type === 'trading_file' ? selectedSource1Mode : undefined
                });
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select source..." />
            </SelectTrigger>
            <SelectContent>
              {availableSources.map((source) => (
                <SelectItem key={source.id} value={source.id}>
                  {selectedSource1Type === 'trading_file' 
                    ? (source as any).filename 
                    : `${(source as any).name} (${(source as any).filename})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedSource1Type === 'trading_file' && selectedFile && (
          <div>
            <label className="text-sm font-medium">Analysis Mode</label>
            <Select 
              value={selectedSource1Mode} 
              onValueChange={(value) => {
                setSelectedSource1Mode(value);
                if (source1) {
                  setSource1({ ...source1, analysis_mode: value });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {selectedFile.analysis_modes.map((mode) => (
                  <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    );
  };

  const renderSource2Selection = () => {
    const availableSources = selectedSource2Type === 'trading_file' ? tradingFiles : honestSimulations;
    const selectedFile = selectedSource2Type === 'trading_file' 
      ? tradingFiles.find(f => f.id === source2?.id)
      : null;

    return (
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium">Source 2 Type</label>
          <Select 
            value={selectedSource2Type} 
            onValueChange={(value: 'trading_file' | 'honest_simulation') => {
              setSelectedSource2Type(value);
              setSource2(null);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="trading_file">Trading Pattern File</SelectItem>
              <SelectItem value="honest_simulation">Honest Backtest Simulation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium">
            {selectedSource2Type === 'trading_file' ? 'Trading File' : 'Simulation'}
          </label>
          <Select 
            value={source2?.id || ''} 
            onValueChange={(value) => {
              const sourceData = availableSources.find(s => s.id === value);
              if (sourceData) {
                setSource2({
                  type: selectedSource2Type,
                  id: value,
                  name: selectedSource2Type === 'trading_file' 
                    ? (sourceData as any).filename 
                    : (sourceData as any).name,
                  analysis_mode: selectedSource2Type === 'trading_file' ? selectedSource2Mode : undefined
                });
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select source..." />
            </SelectTrigger>
            <SelectContent>
              {availableSources.map((source) => (
                <SelectItem key={source.id} value={source.id}>
                  {selectedSource2Type === 'trading_file' 
                    ? (source as any).filename 
                    : `${(source as any).name} (${(source as any).filename})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedSource2Type === 'trading_file' && selectedFile && (
          <div>
            <label className="text-sm font-medium">Analysis Mode</label>
            <Select 
              value={selectedSource2Mode} 
              onValueChange={(value) => {
                setSelectedSource2Mode(value);
                if (source2) {
                  setSource2({ ...source2, analysis_mode: value });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {selectedFile.analysis_modes.map((mode) => (
                  <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'identical':
        return <Equal className="h-4 w-4 text-green-500" />;
      case 'different_values':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'only_in_source1':
        return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'only_in_source2':
        return <TrendingDown className="h-4 w-4 text-purple-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'identical':
        return <Badge variant="default" className="bg-green-100 text-green-800">Identical</Badge>;
      case 'different_values':
        return <Badge variant="default" className="bg-yellow-100 text-yellow-800">Different Values</Badge>;
      case 'only_in_source1':
        return <Badge variant="default" className="bg-blue-100 text-blue-800">Only in Source 1</Badge>;
      case 'only_in_source2':
        return <Badge variant="default" className="bg-purple-100 text-purple-800">Only in Source 2</Badge>;
      default:
        return null;
    }
  };

  // Filter and sort logic
  const filterAndSortResults = (results: PatternComparisonResult[]) => {
    let filtered = results.filter(result => {
      // Status filter
      if (statusFilter !== 'all' && result.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchQuery && !result.pattern.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Success rate filters
      const source1SuccessRate = result.source1_data?.success_rate;
      const source2SuccessRate = result.source2_data?.success_rate;
      const maxSuccessRateValue = Math.max(source1SuccessRate || 0, source2SuccessRate || 0);
      
      if (minSuccessRate && maxSuccessRateValue < parseFloat(minSuccessRate)) {
        return false;
      }
      if (maxSuccessRate && maxSuccessRateValue > parseFloat(maxSuccessRate)) {
        return false;
      }

      // Frequency filters
      const source1Frequency = result.source1_data?.frequency;
      const source2Frequency = result.source2_data?.frequency;
      const maxFrequencyValue = Math.max(source1Frequency || 0, source2Frequency || 0);
      
      if (minFrequency && maxFrequencyValue < parseFloat(minFrequency)) {
        return false;
      }
      if (maxFrequency && maxFrequencyValue > parseFloat(maxFrequency)) {
        return false;
      }

      return true;
    });

    // Sort results
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'pattern':
          aValue = a.pattern;
          bValue = b.pattern;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'source1_frequency':
          aValue = a.source1_data?.frequency || 0;
          bValue = b.source1_data?.frequency || 0;
          break;
        case 'source2_frequency':
          aValue = a.source2_data?.frequency || 0;
          bValue = b.source2_data?.frequency || 0;
          break;
        case 'source1_success':
          aValue = a.source1_data?.success_rate || 0;
          bValue = b.source1_data?.success_rate || 0;
          break;
        case 'source2_success':
          aValue = a.source2_data?.success_rate || 0;
          bValue = b.source2_data?.success_rate || 0;
          break;
        default:
          aValue = a.pattern;
          bValue = b.pattern;
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    return filtered;
  };

  const clearAllFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setMinSuccessRate('');
    setMaxSuccessRate('');
    setMinFrequency('');
    setMaxFrequency('');
    setSortField('pattern');
    setSortDirection('asc');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (statusFilter !== 'all') count++;
    if (searchQuery) count++;
    if (minSuccessRate || maxSuccessRate) count++;
    if (minFrequency || maxFrequency) count++;
    return count;
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center space-y-2">
          <BarChart3 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">Loading available sources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Cross Pattern Comparison
          </CardTitle>
          <CardDescription>
            Compare patterns between trading files and honest backtest simulations to identify differences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-6`}>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Source 1</h3>
              {renderSource1Selection()}
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Source 2</h3>
              {renderSource2Selection()}
            </div>
          </div>

          <div className="flex justify-center">
            <Button 
              onClick={handleComparePatterns} 
              disabled={!source1 || !source2 || isLoading}
              className="w-full max-w-md"
            >
              {isLoading ? (
                <>
                  <BarChart3 className="h-4 w-4 mr-2 animate-spin" />
                  Comparing Patterns...
                </>
              ) : (
                <>
                  <GitCompare className="h-4 w-4 mr-2" />
                  Compare Patterns
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {comparisonResult && (
        <div className="space-y-6">
          {/* Summary Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>Comparison Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
                <div className="text-center space-y-2">
                  <div className="text-2xl font-bold text-green-600">{comparisonResult.identical_patterns}</div>
                  <div className="text-sm text-muted-foreground">Identical Patterns</div>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-2xl font-bold text-yellow-600">{comparisonResult.different_value_patterns}</div>
                  <div className="text-sm text-muted-foreground">Different Values</div>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-2xl font-bold text-blue-600">{comparisonResult.only_in_source1}</div>
                  <div className="text-sm text-muted-foreground">Only in Source 1</div>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-2xl font-bold text-purple-600">{comparisonResult.only_in_source2}</div>
                  <div className="text-sm text-muted-foreground">Only in Source 2</div>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-2xl font-bold">{comparisonResult.total_patterns_source1}</div>
                  <div className="text-sm text-muted-foreground">Total in Source 1</div>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-2xl font-bold">{comparisonResult.total_patterns_source2}</div>
                  <div className="text-sm text-muted-foreground">Total in Source 2</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Comparison Results */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Pattern Comparison</CardTitle>
              <CardDescription>
                {(() => {
                  const filteredResults = filterAndSortResults(comparisonResult.comparison_details);
                  return `Showing ${filteredResults.length} of ${comparisonResult.comparison_details.length} unique patterns`;
                })()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filter Controls */}
              <div className="space-y-4 border-b pb-4">
                {/* Quick Filter Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={statusFilter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('all')}
                  >
                    All Patterns ({comparisonResult.comparison_details.length})
                  </Button>
                  <Button
                    variant={statusFilter === 'identical' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('identical')}
                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                  >
                    <Equal className="h-3 w-3 mr-1" />
                    Identical ({comparisonResult.identical_patterns})
                  </Button>
                  <Button
                    variant={statusFilter === 'different_values' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('different_values')}
                    className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200"
                  >
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Different ({comparisonResult.different_value_patterns})
                  </Button>
                  <Button
                    variant={statusFilter === 'only_in_source1' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('only_in_source1')}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  >
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Source 1 ({comparisonResult.only_in_source1})
                  </Button>
                  <Button
                    variant={statusFilter === 'only_in_source2' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('only_in_source2')}
                    className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                  >
                    <TrendingDown className="h-3 w-3 mr-1" />
                    Source 2 ({comparisonResult.only_in_source2})
                  </Button>
                </div>

                {/* Search and Advanced Filters */}
                <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-4'} gap-4`}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search patterns..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Min Success Rate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={minSuccessRate}
                      onChange={(e) => setMinSuccessRate(e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Max Success Rate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={maxSuccessRate}
                      onChange={(e) => setMaxSuccessRate(e.target.value)}
                      className="text-sm"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Input
                      placeholder="Min Frequency"
                      type="number"
                      min="0"
                      value={minFrequency}
                      onChange={(e) => setMinFrequency(e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Max Frequency"
                      type="number"
                      min="0"
                      value={maxFrequency}
                      onChange={(e) => setMaxFrequency(e.target.value)}
                      className="text-sm"
                    />
                  </div>

                  <div className="flex gap-2">
                    {getActiveFilterCount() > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearAllFilters}
                        className="flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Clear ({getActiveFilterCount()})
                      </Button>
                    )}
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Filter className="h-3 w-3" />
                      {filterAndSortResults(comparisonResult.comparison_details).length} results
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSort('pattern')}
                          className="h-auto p-0 font-medium"
                        >
                          Pattern
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSort('status')}
                          className="h-auto p-0 font-medium"
                        >
                          Status
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSort('source1_frequency')}
                          className="h-auto p-0 font-medium"
                        >
                          Source 1 Freq
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSort('source2_frequency')}
                          className="h-auto p-0 font-medium"
                        >
                          Source 2 Freq
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSort('source1_success')}
                          className="h-auto p-0 font-medium"
                        >
                          Source 1 Success
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSort('source2_success')}
                          className="h-auto p-0 font-medium"
                        >
                          Source 2 Success
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>Source 1 Total Seq</TableHead>
                      <TableHead>Source 2 Total Seq</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filterAndSortResults(comparisonResult.comparison_details).map((result, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-sm">
                          {result.pattern}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(result.status)}
                            {getStatusBadge(result.status)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {result.source1_data?.frequency ?? '-'}
                        </TableCell>
                        <TableCell>
                          {result.source2_data?.frequency ?? '-'}
                        </TableCell>
                        <TableCell>
                          {result.source1_data?.success_rate ? `${result.source1_data.success_rate}%` : '-'}
                        </TableCell>
                        <TableCell>
                          {result.source2_data?.success_rate ? `${result.source2_data.success_rate}%` : '-'}
                        </TableCell>
                        <TableCell>
                          {result.source1_data?.total_sequences ?? '-'}
                        </TableCell>
                        <TableCell>
                          {result.source2_data?.total_sequences ?? '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};